import 'moment-timezone'
import moment, { Moment } from 'moment/moment'

export const DEFAULT_DATE_LOCALE = 'en-US'
export const SHORT_DAY_FORMAT = 'D' // 1
export const SHORT_DAY_YEAR_FORMAT = 'D, YYYY' // 1, 2025
export const SHORT_MONTH_DAY_FORMAT = 'MMM D' // Jan 1
export const SHORT_MONTH_DOW_FORMAT = 'MMM, ddd' // Jan, Sun
export const SHORT_DOW_FORMAT = 'ddd' // Sun
export const SHORT_DOW_MONTH_DAY_FORMAT = 'ddd, MMM Do' // Sun, Jan 1st
export const DOW_MONTH_DAY_FORMAT = 'dddd, MMM Do' // Sunday, Jan 1st
export const SHORT_MONTH_FORMAT = 'MMM' // Jan
export const SHORT_MONTH_YEAR_FORMAT = 'MMM YYYY' // Jan 2025
export const MONTH_FORMAT = 'MMMM' // January
export const MONTH_YEAR_FORMAT = 'MMMM YYYY' // January 2025
export const SHORT_DATE_FORMAT = 'MMM D, YYYY' // Jan 1, 2025
export const DATE_FORMAT = 'MMM Do, yyyy' // Jan 1st, 2025
export const SHORT_DOW_DATE_FORMAT = 'ddd, MMM Do YYYY' // Sun, Jan 1st 2025
export const DOW_DATE_FORMAT = 'dddd, MMM Do YYYY' // Sunday, Jan 1st 2025
export const DOW_DATE_LONG_FORMAT = 'dddd, MMMM Do YYYY' // Sunday, January 1st 2025
export const DATE_KEY_FORMAT = 'YYYYMMDD' // 20250101
export const DATE_KEY_MONTH_FORMAT = 'YYYYMM' // 202501
export const DATE_SHORT_ISO_FORMAT = 'YYYY-MM-DD' // 2025-01-01
export const DATE_ISO_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ' // 2025-01-01T00:00:00-05:00
export const TIME_FORMAT = 'h:mm A' // 5:00 AM
export const DAYS_OF_WEEK = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
]
export const DAILY_HOURS = getDailyHours()

function getDailyHours() {
    const N = Array.from(Array(12).keys())
    return [...N.map((h) => `${h || 12}:00 AM`), ...N.map((h) => `${h || 12}:00 PM`)]
}

export const dateFormat = (date: moment.MomentInput, format = DATE_FORMAT) =>
    moment(date).format(format)

export const dateFromDateString = (dateString: string | null, format = DATE_SHORT_ISO_FORMAT) => {
    if (dateString === null) return moment()
    const parsedDate = moment(dateString, format, true)
    return parsedDate.isValid() ? parsedDate : moment()
}

export const isPast = (date: moment.MomentInput): boolean => {
    return moment(date).isBefore(moment())
}

export const isSameDMY = (date1: moment.MomentInput, date2: moment.MomentInput): boolean => {
    return (
        moment(date1).isSame(moment(date2), 'day') &&
        moment(date1).isSame(moment(date2), 'month') &&
        moment(date1).isSame(moment(date2), 'year')
    )
}

export const isThisYear = (date: moment.MomentInput): boolean => {
    return moment(date).isSame(moment(), 'year')
}

export const isThisMonth = (date: moment.MomentInput): boolean => {
    return moment(date).isSame(moment(), 'month')
}

export const isToday = (date: moment.MomentInput): boolean => {
    return moment(date).isSame(moment(), 'day')
}

export const isDayInMonth = (day: moment.MomentInput, month: number): boolean => {
    return moment(day).month() === month
}

export const dateByMovingToEndOfDay = (date: moment.MomentInput): Date => {
    return moment(date).set({ hour: 23, minute: 59, second: 59 }).toDate()
}

export const getMinutesFromStartOfDay = (date: moment.MomentInput): number => {
    return moment(date).diff(moment(date).clone().startOf('day'), 'minutes')
}

export const getMinutesToEndOfDay = (date: moment.MomentInput): number => {
    return moment(date).clone().endOf('day').diff(moment(date), 'minutes')
}

export const getDurationInMinutes = (
    start: moment.MomentInput,
    end: moment.MomentInput
): number => {
    return moment.duration(moment(end).diff(start)).asMinutes()
}

export const getTimeFromMinutes = (minutes: number): string => {
    return moment.utc(minutes * 60_000).format(TIME_FORMAT)
}

export const areTimesWithinFifteenMinutes = (
    time1: moment.MomentInput,
    time2: moment.MomentInput
): boolean => {
    return moment(time1, TIME_FORMAT).isBetween(
        moment(time2, TIME_FORMAT).subtract(15, 'minutes'),
        moment(time2, TIME_FORMAT).add(15, 'minutes')
    )
}

export const getDaysLeftInMonth = (date: moment.MomentInput): number => {
    return moment(date).endOf('month').diff(moment(date), 'days')
}

export const formatTimeStamp = (date?: moment.Moment): string => {
    return date ? date.format('YYYY-MM-DDTHH:mm:ssZ') : ''
}

export const momentComparator = (m1?: Moment, m2?: Moment) => {
    if (!m1 || !m2) return 0
    return m1.isBefore(m2) ? -1 : m1.isAfter(m2) ? 1 : 0
}

export const getFirstDayOfWeek = (date: Date): Date => {
    const currentDay = date.getDay() // Get the current day of the week (0-6, Sunday is 0)
    const firstDayOfWeek = new Date(date) // Clone the date to avoid modifying the original date
    firstDayOfWeek.setDate(date.getDate() - currentDay) // Subtract the day of the week from the date
    return firstDayOfWeek
}
export const getLastDayOfWeek = (date: Date): Date => {
    const currentDay = date.getDay()
    const lastDayOfWeek = new Date(date)
    const daysToAdd = 6 - currentDay
    lastDayOfWeek.setDate(lastDayOfWeek.getDate() + daysToAdd)
    return lastDayOfWeek
}

export const getFirstDayOfMonth = (date: Date): Date => {
    const firstDay = new Date(date)
    firstDay.setDate(1)
    return firstDay
}

export const getLastDayOfMonth = (date: Date): Date => {
    const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0)
    return lastDayOfMonth
}

export const isFirstDayOfMonth = (date: Date): boolean => {
    return date.getDate() === 1
}

export const isLastDayOfMonth = (date: Date): boolean => {
    const nextMonth = new Date(date.getFullYear(), date.getMonth() + 1, 1)
    const lastDayOfCurrentMonth = new Date(nextMonth.setDate(nextMonth.getDate() - 1))
    return date.getDate() === lastDayOfCurrentMonth.getDate()
}

export const getFirstDayOfNextMonth = (date: Date): Date => {
    const firstDayNextMonth = new Date(date)
    firstDayNextMonth.setMonth(date.getMonth() + 1)
    firstDayNextMonth.setDate(1)
    return firstDayNextMonth
}

export const getFirstDayOfQuarter = (date: Date): Date => {
    const month = date.getMonth()
    const firstMonthOfQuarter = month - (month % 3)
    const firstDayOfQuarter = new Date(date.getFullYear(), firstMonthOfQuarter, 1)
    return firstDayOfQuarter
}

export const getLastDayOfQuarter = (date: Date): Date => {
    const month = date.getMonth()
    const lastMonthOfQuarter = month - (month % 3) + 2
    const lastDayOfQuarter = new Date(date.getFullYear(), lastMonthOfQuarter + 1, 0)
    return lastDayOfQuarter
}

export const getFirstDayOfYear = (date: Date): Date => {
    return new Date(date.getFullYear(), 0, 1)
}

export const getLastDayOfYear = (date: Date): Date => {
    return new Date(date.getFullYear(), 11, 31)
}

export const isSameMonthAndYear = (startDate: Date, endDate: Date): boolean => {
    const startYear = startDate.getFullYear()
    const startMonth = startDate.getMonth()
    const endYear = endDate.getFullYear()
    const endMonth = endDate.getMonth()
    return startYear === endYear && startMonth === endMonth
}

export const isSameYear = (startDate: Date, endDate: Date): boolean => {
    const startYear = startDate.getFullYear()
    const endYear = endDate.getFullYear()
    return startYear === endYear
}

export const isDayChecked = (dow: number | undefined, day: string | undefined) => {
    if (!dow || !day) return false
    const dayInt = parseInt(day)
    return Boolean(dow & dayInt)
}

// Given a time formatted as a decimal number between 0 and 24, where the decimal part is either 0 or 0.5,
// this returns the hour, minute, and AM/PM value. Hour will be in the range 0-11, and minute will be 0 or 30.
export const timeComponentsFromDecimalTime = (
    decimalTime: number
): [number, number, 'AM' | 'PM'] => {
    return [
        Math.floor(decimalTime % 12),
        Math.floor(decimalTime) === decimalTime ? 0 : 30,
        decimalTime < 12 ? 'AM' : 'PM',
    ]
}

// Given two dates, produces a string description of the span between them, without times.
// If the dates are the same day, it returns just that day.
// Otherwise, it returns a result like `Jul 9, 2024 - Oct 14, 2025`
// If the dates are the same year or month then those components aren't repeated.
// e.g.: `Jul 9 - 15, 2025`
export const dateSpanDescription = (lhs: moment.Moment, rhs: moment.Moment): string => {
    if (isSameDMY(lhs, rhs)) {
        return lhs.format(SHORT_DATE_FORMAT)
    }

    if (isSameMonthAndYear(lhs.toDate(), rhs.toDate())) {
        return lhs.format('MMM D') + ' - ' + rhs.format('D, YYYY')
    }

    if (isSameYear(lhs.toDate(), rhs.toDate())) {
        return lhs.format('MMM D') + ' - ' + rhs.format('MMM D, YYYY')
    }

    return lhs.format('MMM D, YYYY') + ' - ' + rhs.format('MMM D, YYYY')
}
